import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, LoadingState, ErrorState, EmptyState, StatusBadge } from "@/components/common";
import { PlanGate } from "@/components/PlanGate";
import { NewInvoiceDialog } from "@/modules/invoices/InvoiceDialogs";
import { QuickChips } from "@/components/layout/Record";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { fmtCents, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const FILTERS = ["", "draft", "unpaid", "paid", "credited", "void", "credit_notes"];

function List() {
  const { readOnly } = useGarage();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const [open, setOpen] = useState(() => params.get("new") === "1");
  const navigate = useNavigate();
  const cn = status === "credit_notes";
  const cust = params.get("customer_id") || "";
  const { data, error, loading, reload } = useFetch("/invoices", cn ? { kind: "credit_note", customer_id: cust } : { status, customer_id: cust });
  return (
    <div>
      <PageHeader title={t("invoices.title")} subtitle={t("invoices.subtitle")}
        actions={!readOnly && <Button onClick={() => setOpen(true)} data-testid="new-invoice-button"><Plus className="mr-2 h-4 w-4" /> {t("invoices.new")}</Button>} />
      {params.get("pay") === "1" && <p className="mb-4 rounded-lg bg-teal-50 p-3 text-sm text-teal-900" data-testid="pay-hint">{t("invoices.pickToPay")}</p>}
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <QuickChips testPrefix="invoices-filter" value={status} onChange={(s) => setParams(s ? { status: s } : {})} options={FILTERS.map((s) => [s, t(`invoices.filters.${s || "all"}`)])} />
        {cust && <button type="button" onClick={() => setParams(status ? { status } : {})} className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="invoices-clear-scope">{t("b13.filtered")} ×</button>}
      </div>
      {loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {data?.length === 0 && <EmptyState icon={Receipt} title={t("invoices.empty")} text={t("invoices.emptyText")} testId="invoices-empty" />}
      {data?.length > 0 && (
        <div className="gf-card divide-y divide-slate-100" data-testid="invoices-list">
          {data.map((i) => (
            <button key={i.id} type="button" onClick={() => navigate(`/pro/invoices/${i.id}`)} data-testid={`invoice-row-${i.number || i.id}`}
              className="grid w-full grid-cols-2 items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 md:grid-cols-[140px_1fr_1fr_130px_130px]">
              <span className="font-mono text-sm font-semibold">{i.number || t("invoices.draft")}{i.invoice_number && <span className="block text-[11px] font-normal text-slate-500">{t("invoices.forInvoice", { number: i.invoice_number })}</span>}</span>
              <span className="text-sm">{i.customer.name}<span className="block text-xs text-slate-500">{i.vehicle?.plate}</span></span>
              <span className="text-sm text-slate-600">{i.issue_date ? fmtYmd(i.issue_date) : "—"}{i.due_date && <span className={`block text-xs ${i.overdue ? "font-semibold text-red-600" : ""}`}>{t("invoices.due", { date: fmtYmd(i.due_date) })}</span>}</span>
              <span className="text-sm md:text-right"><strong>{fmtCents(i.total_cents)}</strong>{i.kind === "invoice" && i.balance_cents > 0 && i.status !== "draft" && <span className="block text-xs text-amber-700">{t("invoices.balance", { amount: fmtCents(i.balance_cents) })}</span>}</span>
              <span className="md:text-right"><StatusBadge status={i.overdue ? "overdue" : i.status} /></span>
            </button>
          ))}
        </div>
      )}
      {open && <NewInvoiceDialog onClose={() => setOpen(false)} onDone={(inv) => navigate(`/pro/invoices/${inv.id}`)} />}
    </div>
  );
}

export default function Invoices() {
  return <PlanGate feature="quotes_and_invoices" managers><List /></PlanGate>;
}
